/**
 * Social Login Buttons
 *
 * Reusable OAuth provider buttons (Google, Apple, Microsoft).
 * 3-column grid with branded SVG icons, loading states, and dark mode support.
 */

import { useState } from 'react';

import { useAuth } from '../providers/AuthProvider';

import type { OAuthProvider } from '../lib/auth';

interface SocialLoginButtonsProps {
  dividerText?: string;
}

/**
 *
 */
export function SocialLoginButtons({ dividerText = 'or continue with' }: SocialLoginButtonsProps) {
  const { loginWithOAuth } = useAuth();
  const [loadingProvider, setLoadingProvider] = useState<OAuthProvider | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleOAuth = (provider: OAuthProvider) => {
    setError(null);
    setLoadingProvider(provider);
    // signInWithRedirect navigates the browser away — don't await it.
    // Only catch true failures (config errors, storage issues).
    loginWithOAuth(provider).catch((err: unknown) => {
      console.error('[OAuth] redirect failed:', err);
      setError(`Failed to sign in with ${provider}. Please try again.`);
      setLoadingProvider(null);
    });
  };

  const isDark = typeof document !== 'undefined' && document.documentElement.classList.contains('dark');

  return (
    <div style={{ marginTop: '24px' }}>
      {/* Divider */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
        <div style={{ flex: 1, height: '1px', backgroundColor: 'hsl(var(--color-neutral-200))' }} />
        <span
          style={{
            fontSize: '13px',
            color: 'hsl(var(--color-neutral-400))',
            whiteSpace: 'nowrap',
            textTransform: 'lowercase',
          }}
        >
          {dividerText}
        </span>
        <div style={{ flex: 1, height: '1px', backgroundColor: 'hsl(var(--color-neutral-200))' }} />
      </div>

      {/* Error */}
      {error && (
        <div
          style={{
            marginBottom: '12px',
            padding: '8px 12px',
            borderRadius: '6px',
            fontSize: '13px',
            backgroundColor: 'hsl(var(--color-error-50))',
            color: 'hsl(var(--color-error-700))',
          }}
          role="alert"
        >
          {error}
        </div>
      )}

      {/* Button grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
        {(['Google', 'Apple', 'Microsoft'] as const).map((provider) => {
          const isLoading = loadingProvider === provider;
          const isDisabled = loadingProvider !== null && !isLoading;

          return (
            <button
              key={provider}
              onClick={() => void handleOAuth(provider)}
              disabled={isDisabled || isLoading}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '10px 4px',
                borderRadius: '8px',
                border: '1px solid hsl(var(--color-neutral-200))',
                backgroundColor: 'hsl(var(--color-neutral-50))',
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                opacity: isDisabled ? 0.4 : 1,
                transition: 'all 150ms ease',
                fontSize: '13px',
                fontWeight: 500,
                color: 'hsl(var(--color-neutral-700))',
              }}
              onMouseEnter={(e) => {
                if (!isDisabled && !isLoading) {
                  e.currentTarget.style.backgroundColor = 'hsl(var(--color-neutral-100))';
                  e.currentTarget.style.borderColor = 'hsl(var(--color-neutral-300))';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'hsl(var(--color-neutral-50))';
                e.currentTarget.style.borderColor = 'hsl(var(--color-neutral-200))';
              }}
            >
              {isLoading ? (
                <LoadingSpinner />
              ) : (
                <>
                  <ProviderIcon provider={provider} isDark={isDark} />
                  <span>{provider}</span>
                </>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function LoadingSpinner() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      style={{ animation: 'spin 1s linear infinite' }}
    >
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <circle cx="12" cy="12" r="10" stroke="hsl(var(--color-neutral-300))" strokeWidth="3" />
      <path
        d="M12 2a10 10 0 0 1 10 10"
        stroke="hsl(var(--color-neutral-600))"
        strokeWidth="3"
        strokeLinecap="round"
      />
    </svg>
  );
}

function ProviderIcon({ provider, isDark }: { provider: OAuthProvider; isDark: boolean }) {
  switch (provider) {
    case 'Google':
      return (
        <svg width="18" height="18" viewBox="0 0 24 24">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A10 10 0 0 0 1.14 12c0 1.62.39 3.15 1.04 4.5l3.66-2.41z" fill="#FBBC05" />
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
        </svg>
      );
    case 'Apple':
      return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill={isDark ? '#ffffff' : '#000000'}>
          <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
        </svg>
      );
    case 'Microsoft':
      return (
        <svg width="18" height="18" viewBox="0 0 23 23">
          <rect x="1" y="1" width="10" height="10" fill="#F25022" />
          <rect x="12" y="1" width="10" height="10" fill="#7FBA00" />
          <rect x="1" y="12" width="10" height="10" fill="#00A4EF" />
          <rect x="12" y="12" width="10" height="10" fill="#FFB900" />
        </svg>
      );
  }
}
